/**
 * ⟦C57 · lot 5d · A-174⟧ **Une ligne du Journal reste une ligne courte.**
 *
 * Le Journal garde deux sortes d'entrées. Celles dont le fait a un détail (`JournalEntry.detail`,
 * lot 1) portent déjà leur ligne courte et leur bouton « Les dessous ». Les AUTRES (les lignes du
 * `log` du moteur, écrites avant le chantier ou sans détail : « Tu as publié. Tu lèves les yeux : il
 * y a une fenêtre dans cette chambre. Regarder dehors… ») peuvent faire trois phrases : à l'écran,
 * le Journal n'en montre alors que la PREMIÈRE, et le pictogramme 📰 rouvre le reste.
 *
 * **Rien n'est supprimé, rien n'est réécrit.** Le texte de l'entrée reste intact dans la run (c'est
 * lui que la recherche lit et que les autres consommateurs affichent) ; ce module ne fait que
 * COUPER à l'affichage, jamais au milieu d'une idée quand une frontière existe : fin de phrase
 * d'abord, puis `:` `,` `;` `—`, puis, faute de mieux, le douzième mot (avec « … », et dans ce cas
 * les dessous portent le texte ENTIER).
 *
 * Pur : aucun DOM, aucun store. Le mètre est celui de l'écran (`game/dessous.ts`).
 */

import { MOTS_PHRASE_MAX, compterMots } from "../../game/dessous";

/**
 * Les phrases d'une ligne : coupées après `.` `!` `?` `…`, et après `»` SEULEMENT si une majuscule
 * suit (une nouvelle phrase). `phrasesDe` (le mètre de l'écran) coupe aussi après un `»` suivi d'une
 * minuscule ou d'une parenthèse, ce qui scinderait « Publié : « X » (trend, 128 vues attendues). » en
 * deux : ici, une citation au milieu d'une phrase ne la coupe pas.
 */
function phrasesDeLaLigne(texte: string): string[] {
  return texte
    .split(/(?<=[.!?…])\s+|(?<=»)\s+(?=\p{Lu})/u)
    .filter((p) => /[\p{L}\p{N}]/u.test(p));
}

export interface LigneCourte {
  /** Ce que la ligne affiche : une phrase de 12 mots au plus. */
  courte: string;
  /**
   * Ce qui part dans les dessous : le texte entier si la ligne a été coupée au milieu d'une phrase,
   * sinon les phrases qui suivent la première. `null` quand la ligne tient déjà : pas de bouton.
   */
  reste: string | null;
  /** `true` si la coupe est tombée au milieu d'une phrase : `reste` est alors le texte entier. */
  coupeeAuMilieu: boolean;
}

/** Les jetons qui terminent un groupe de mots : on coupe volontiers après eux. */
const FIN_DE_GROUPE = /[,:;]$/u;

export function ligneCourte(texte: string): LigneCourte {
  const t = texte.trim();
  const phrases = phrasesDeLaLigne(t);
  if (phrases.length <= 1 && compterMots(t) <= MOTS_PHRASE_MAX) {
    return { courte: t, reste: null, coupeeAuMilieu: false };
  }

  const premiere = phrases[0] ?? t;
  if (compterMots(premiere) <= MOTS_PHRASE_MAX) {
    // La coupe tombe sur une fin de phrase : la ligne est la première, les dessous la suite.
    return {
      courte: premiere,
      reste: phrases.slice(1).join(" "),
      coupeeAuMilieu: false,
    };
  }

  // La première phrase dépasse 12 mots : on coupe à une frontière naturelle, sinon au douzième mot.
  const jetons = premiere.split(/\s+/u);
  let vus = 0;
  let douzieme = jetons.length;
  let derniereFrontiere = -1;
  for (const [i, jeton] of jetons.entries()) {
    if (compterMots(jeton) > 0) vus += 1;
    if (vus > MOTS_PHRASE_MAX) {
      douzieme = i;
      break;
    }
    if (vus >= 3 && (FIN_DE_GROUPE.test(jeton) || jetons[i + 1] === "—")) derniereFrontiere = i + 1;
  }
  const fin = derniereFrontiere > 0 ? derniereFrontiere : douzieme;
  const coupe = jetons
    .slice(0, fin)
    .join(" ")
    .replace(/[\s,;:—]+$/u, "");
  return { courte: `${coupe}…`, reste: t, coupeeAuMilieu: true };
}
